import cx from 'classnames';
import { useState } from 'react';
import { Button, ButtonVariants } from '@components/button/Button';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { errorToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { parseToShortDateRange } from '@shared/utils/dateFormatters';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import typography from '~styles/typography.scss';
import { getOperaCandidates } from './apiHelpers';
import { useLinkOperaReservation } from './hooks';
import styles from './HotelLinkModal.scss';
import type { OperaCandidateDTO } from './types';

export interface HotelLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  reservationId: string;
  onLinked?: () => void;
}

export const HotelLinkModal = ({
  isOpen,
  onClose,
  reservationId,
  onLinked,
}: HotelLinkModalProps) => {
  const { id: restaurantId } = useRestaurant();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<OperaCandidateDTO[]>([]);
  const [selectedCandidate, setSelectedCandidate] =
    useState<OperaCandidateDTO | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hotelUnavailable, setHotelUnavailable] = useState(false);

  const linkMutation = useLinkOperaReservation(reservationId, () => {
    handleClose();
    onLinked?.();
  });

  const noResultsFound = !isSearching && hasSearched && results.length === 0;

  const handleSearch = async () => {
    setIsSearching(true);
    setSearchError(null);
    setSelectedCandidate(null);

    try {
      const response = await getOperaCandidates(restaurantId, reservationId, {
        query: searchQuery.trim(),
      });
      setResults(response.candidates);
      setHotelUnavailable(response.hotelUnavailable);
      setHasSearched(true);
    } catch (e) {
      const message =
        e instanceof ApiError
          ? e.message
          : 'Failed to search hotel reservations.';
      setSearchError(message);
      errorToast({ message });
    } finally {
      setIsSearching(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setResults([]);
    setSelectedCandidate(null);
    setHasSearched(false);
    setSearchError(null);
    setHotelUnavailable(false);
    onClose();
  };

  const handleConfirm = () => {
    if (!selectedCandidate) return;
    linkMutation.mutate(selectedCandidate.operaReservationId);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Link to Hotel Reservation"
      subtitle="Search by name, email, or phone to find a matching hotel stay."
    >
      <div className={styles.content}>
        <form
          className={styles.searchForm}
          onSubmit={(e) => {
            e.preventDefault();
            void handleSearch();
          }}
        >
          <div className={styles.searchInput}>
            <label className={typography.c2_20} htmlFor="hotel-link-query">
              Search for a guest
            </label>
            <input
              id="hotel-link-query"
              type="text"
              placeholder="Name, email, or phone"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            variant={ButtonVariants.Primary}
            label="Search"
            type="submit"
            isLoading={isSearching}
            // Mirrors the route's own min(3): a shorter query is a 400, not a search.
            isDisabled={searchQuery.trim().length < 3}
          />
        </form>

        {searchError && <div className={styles.errorState}>{searchError}</div>}

        {noResultsFound && (
          <div className={styles.emptyState}>
            {hotelUnavailable
              ? 'Could not reach the hotel; try again'
              : 'No matching hotel reservations found.'}
          </div>
        )}

        {!isSearching && results.length > 0 && (
          <div className={styles.results}>
            {results.map((candidate) => (
              <button
                type="button"
                key={candidate.operaReservationId}
                className={cx(styles.resultRow, {
                  [styles.resultRowSelected]:
                    selectedCandidate?.operaReservationId ===
                    candidate.operaReservationId,
                })}
                onClick={() => setSelectedCandidate(candidate)}
              >
                <span className={styles.guestName}>{candidate.guestName}</span>
                <span className={styles.candidateDetail}>
                  {parseToShortDateRange(
                    candidate.arrivalDate,
                    candidate.departureDate,
                  )}
                </span>
                <span className={styles.candidateDetail}>
                  {candidate.reservationStatus}
                </span>
                <span className={styles.candidateDetail}>
                  {candidate.roomNumber
                    ? `Room ${candidate.roomNumber}`
                    : 'No room assigned'}
                </span>
                {candidate.vipCode && (
                  <span className={styles.candidateDetail}>
                    VIP: {candidate.vipCode}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {selectedCandidate && (
          <ModalActions className={styles.modalActions}>
            <Button
              variant={ButtonVariants.Tertiary}
              label="Cancel"
              onClick={handleClose}
            />
            <Button
              variant={ButtonVariants.Primary}
              label="Confirm"
              onClick={handleConfirm}
              isLoading={linkMutation.isPending}
              isDisabled={linkMutation.isPending}
            />
          </ModalActions>
        )}
      </div>
    </Modal>
  );
};
